import { useState, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Search, Download, Upload, Plus, Pencil, Trash2, Check, X,
  ChevronLeft, ChevronRight, Loader2, AlertCircle, FileUp, Copy,
} from 'lucide-react'
import { toast } from 'sonner'
import { quetureContentApi } from '@/api/quetureContent'
import type { GradePreviewResult, GradePreviewRow, GradeFilterParams, BuildupPreviewResult, BuildupPreviewRow, BuildupStep } from '@/api/quetureContent'
import { QueBoardConfigEditor } from '@/components/QueBoardConfigEditor'
import { BuildupEditor } from '@/components/BuildupEditor'

type Step = 'table' | 'preview' | 'done' | 'buildup-preview' | 'buildup-done'

export default function QuetureGradeManagement() {
  const queryClient = useQueryClient()
  const [queboardEditId, setQueboardEditId] = useState<string | null>(null)
  const [buildupEditId, setBuildupEditId] = useState<string | null>(null)

  // ── Filter state ───────────────────────────────────────────────────
  const [filters, setFilters] = useState<GradeFilterParams>({})
  const toggleFilter = (key: keyof GradeFilterParams) => {
    setFilters(prev => ({ ...prev, [key]: prev[key] ? undefined : true }))
    setPage(0)
  }

  // ── Table state ────────────────────────────────────────────────────
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [page, setPage] = useState(0)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [addingNew, setAddingNew] = useState(false)
  const [newQuetureId, setNewQuetureId] = useState('')
  const [newGrade, setNewGrade] = useState('')

  // ── Upload state ───────────────────────────────────────────────────
  const [step, setStep] = useState<Step>('table')
  const [preview, setPreview] = useState<GradePreviewResult | null>(null)
  const [previewFilter, setPreviewFilter] = useState<string>('all')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  // ── Buildup upload state ──────────────────────────────────────────
  const [buildupPreview, setBuildupPreview] = useState<BuildupPreviewResult | null>(null)
  const [buildupPreviewFilter, setBuildupPreviewFilter] = useState<string>('all')
  const [buildupExpandedId, setBuildupExpandedId] = useState<string | null>(null)
  const buildupFileInputRef = useRef<HTMLInputElement>(null)

  // ── Queries ────────────────────────────────────────────────────────
  const { data: pageData, isLoading } = useQuery({
    queryKey: ['queture-grades', page, search, filters],
    queryFn: () => quetureContentApi.listGrades(page, 20, search || undefined, filters),
  })

  // ── Mutations ──────────────────────────────────────────────────────
  const updateMutation = useMutation({
    mutationFn: ({ quetureId, grade }: { quetureId: string; grade: string }) =>
      quetureContentApi.updateGrade(quetureId, grade),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queture-grades'] })
      setEditingId(null)
      toast.success('수정 완료')
    },
    onError: () => toast.error('수정 실패'),
  })

  const deleteMutation = useMutation({
    mutationFn: (quetureId: string) => quetureContentApi.deleteGrade(quetureId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queture-grades'] })
      toast.success('삭제 완료')
    },
    onError: () => toast.error('삭제 실패'),
  })

  const previewMutation = useMutation({
    mutationFn: (file: File) => quetureContentApi.previewCsv(file),
    onSuccess: (data) => {
      setPreview(data)
      setStep('preview')
    },
    onError: () => toast.error('CSV 파싱 실패'),
  })

  const bulkSaveMutation = useMutation({
    mutationFn: (rows: Array<{ quetureId: string; grade: string }>) =>
      quetureContentApi.bulkSave(rows),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['queture-grades'] })
      setStep('done')
      toast.success(`${result.savedCount}건 저장 완료`)
    },
    onError: () => toast.error('저장 실패'),
  })

  const buildupPreviewMutation = useMutation({
    mutationFn: (file: File) => quetureContentApi.previewBuildupCsv(file),
    onSuccess: (data) => {
      setBuildupPreview(data)
      setStep('buildup-preview')
    },
    onError: () => toast.error('빌드업 CSV 파싱 실패'),
  })

  const buildupBulkSaveMutation = useMutation({
    mutationFn: (rows: Array<{ quetureId: string; steps: BuildupStep[] }>) =>
      quetureContentApi.bulkSaveBuildup(rows),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['queture-grades'] })
      setStep('buildup-done')
      toast.success(`${result.savedCount}건 빌드업 저장 완료`)
    },
    onError: () => toast.error('빌드업 저장 실패'),
  })

  // ── Handlers ───────────────────────────────────────────────────────
  const handleSearch = useCallback(() => {
    setSearch(searchInput)
    setPage(0)
  }, [searchInput])

  const handleFileUpload = useCallback((file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast.error('.csv 파일만 업로드 가능합니다')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('파일 크기는 5MB 이하만 가능합니다')
      return
    }
    previewMutation.mutate(file)
  }, [previewMutation])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFileUpload(file)
  }, [handleFileUpload])

  const handleBulkSave = useCallback(() => {
    if (!preview) return
    const validRows = preview.rows
      .filter((r) => r.status === 'new' || r.status === 'update')
      .map((r) => ({ quetureId: r.quetureId, grade: r.grade }))
    bulkSaveMutation.mutate(validRows)
  }, [preview, bulkSaveMutation])

  const handleAddNew = useCallback(() => {
    if (!newQuetureId.trim() || !newGrade.trim()) {
      toast.error('Queture ID와 학년-단원을 입력해주세요')
      return
    }
    updateMutation.mutate(
      { quetureId: newQuetureId.trim(), grade: newGrade.trim() },
      {
        onSuccess: () => {
          setAddingNew(false)
          setNewQuetureId('')
          setNewGrade('')
        },
      }
    )
  }, [newQuetureId, newGrade, updateMutation])

  // ── Buildup handlers ────────────────────────────────────────────────
  const handleBuildupFileUpload = useCallback((file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast.error('.csv 파일만 업로드 가능합니다')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('파일 크기는 10MB 이하만 가능합니다')
      return
    }
    buildupPreviewMutation.mutate(file)
  }, [buildupPreviewMutation])

  const handleBuildupBulkSave = useCallback(() => {
    if (!buildupPreview) return
    const validRows = buildupPreview.rows
      .filter((r) => r.status === 'new' || r.status === 'update')
      .map((r) => ({ quetureId: r.quetureId, steps: r.steps }))
    buildupBulkSaveMutation.mutate(validRows)
  }, [buildupPreview, buildupBulkSaveMutation])

  const filteredBuildupPreviewRows = buildupPreview?.rows.filter((r) =>
    buildupPreviewFilter === 'all' ? true : r.status === buildupPreviewFilter
  ) ?? []

  const filteredPreviewRows = preview?.rows.filter((r) =>
    previewFilter === 'all' ? true : r.status === previewFilter
  ) ?? []

  const statusBadge = (status: GradePreviewRow['status']) => {
    const styles: Record<string, string> = {
      new: 'bg-emerald-100 text-emerald-700',
      update: 'bg-blue-100 text-blue-700',
      unchanged: 'bg-gray-100 text-gray-500',
      error: 'bg-red-100 text-red-700',
    }
    const labels: Record<string, string> = {
      new: '신규', update: '수정', unchanged: '변경없음', error: '에러',
    }
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    )
  }

  // ── Step: Buildup Done ──────────────────────────────────────────────
  if (step === 'buildup-done') {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="p-12 bg-white rounded-lg border border-gray-200 shadow-sm text-center space-y-4">
          <div className="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center">
            <Check className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">빌드업 저장 완료</h2>
          <p className="text-gray-600">
            {buildupPreview?.newCount ?? 0}건 신규, {buildupPreview?.updateCount ?? 0}건 수정
          </p>
          <button
            onClick={() => { setStep('table'); setBuildupPreview(null) }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            목록으로
          </button>
        </div>
      </div>
    )
  }

  // ── Step: Buildup Preview ─────────────────────────────────────────
  if (step === 'buildup-preview' && buildupPreview) {
    const saveableCount = buildupPreview.newCount + buildupPreview.updateCount
    return (
      <div className="p-6 max-w-5xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">빌드업 CSV 업로드 프리뷰</h1>
          <button
            onClick={() => { setStep('table'); setBuildupPreview(null) }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            취소
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: '신규', count: buildupPreview.newCount, bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', sub: 'text-emerald-600' },
            { label: '수정', count: buildupPreview.updateCount, bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', sub: 'text-blue-600' },
            { label: '스킵 행', count: buildupPreview.skippedRowCount, bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', sub: 'text-gray-600' },
            { label: '에러', count: buildupPreview.errorCount, bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', sub: 'text-red-600' },
          ].map(({ label, count, bg, border, text, sub }) => (
            <div key={label} className={`p-4 ${bg} rounded-lg border ${border} text-center`}>
              <p className={`text-2xl font-bold ${text}`}>{count}</p>
              <p className={`text-sm ${sub}`}>{label}</p>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2">
          {['all', 'new', 'update', 'error'].map((f) => (
            <button
              key={f}
              onClick={() => setBuildupPreviewFilter(f)}
              className={`px-3 py-1 text-sm rounded-lg border ${
                buildupPreviewFilter === f ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {f === 'all' ? `전체 (${buildupPreview.totalQuetureCount})` : f === 'new' ? '신규' : f === 'update' ? '수정' : '에러'}
            </button>
          ))}
        </div>

        {/* Preview table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="max-h-[500px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Queture ID</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-600">스텝 수</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-600">항목 수</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-600">상태</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-600">상세</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredBuildupPreviewRows.map((row) => (
                  <>
                    <tr
                      key={row.quetureId}
                      className={
                        row.status === 'new' ? 'bg-emerald-50/50' :
                        row.status === 'update' ? 'bg-blue-50/50' :
                        row.status === 'error' ? 'bg-red-50/50' : ''
                      }
                    >
                      <td className="px-4 py-2 font-mono text-xs">{row.quetureId}</td>
                      <td className="px-4 py-2 text-center">{row.stepCount}</td>
                      <td className="px-4 py-2 text-center">{row.itemCount}</td>
                      <td className="px-4 py-2 text-center">{statusBadge(row.status as 'new' | 'update' | 'error')}</td>
                      <td className="px-4 py-2 text-center">
                        {row.status === 'error' ? (
                          <span className="text-xs text-red-600">{row.errorMessage}</span>
                        ) : (
                          <button
                            onClick={() => setBuildupExpandedId(buildupExpandedId === row.quetureId ? null : row.quetureId)}
                            className="text-xs text-indigo-600 hover:text-indigo-800"
                          >
                            {buildupExpandedId === row.quetureId ? '접기' : '상세보기'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {/* Expanded detail */}
                    {buildupExpandedId === row.quetureId && (
                      <tr key={`${row.quetureId}-detail`}>
                        <td colSpan={5} className="px-4 py-3 bg-gray-50">
                          <div className="space-y-2">
                            {row.steps.map((s, si) => (
                              <div key={si} className="border border-gray-200 rounded-lg bg-white p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-xs font-semibold text-indigo-600">{s.step}</span>
                                  <span className="text-xs text-gray-600">{s.title || '(제목 없음)'}</span>
                                  <span className="text-xs text-gray-400 ml-auto">{s.items.length}개 항목</span>
                                </div>
                                <div className="space-y-1">
                                  {s.items.map((item, ii) => (
                                    <div key={ii} className="text-xs text-gray-600 pl-4 border-l-2 border-gray-200">
                                      <span className="font-medium text-gray-500">#{item.number}</span>{' '}
                                      <span>{item.instruction || '(지시문 없음)'}</span>
                                      {item.answer && <span className="ml-2 text-emerald-600">정답: {item.answer}</span>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => { setStep('table'); setBuildupPreview(null) }}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={handleBuildupBulkSave}
            disabled={saveableCount === 0 || buildupBulkSaveMutation.isPending}
            className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {buildupBulkSaveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            저장 확인 ({saveableCount}건)
          </button>
        </div>
      </div>
    )
  }

  // ── Step: Done ─────────────────────────────────────────────────────
  if (step === 'done') {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="p-12 bg-white rounded-lg border border-gray-200 shadow-sm text-center space-y-4">
          <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">저장 완료</h2>
          <p className="text-gray-600">
            {preview?.newCount ?? 0}건 신규, {preview?.updateCount ?? 0}건 수정
          </p>
          <button
            onClick={() => { setStep('table'); setPreview(null) }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            목록으로
          </button>
        </div>
      </div>
    )
  }

  // ── Step: Preview ──────────────────────────────────────────────────
  if (step === 'preview' && preview) {
    const saveableCount = preview.newCount + preview.updateCount
    return (
      <div className="p-6 max-w-5xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">학년-단원 CSV 업로드 프리뷰</h1>
          <button
            onClick={() => { setStep('table'); setPreview(null) }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            취소
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: '신규', count: preview.newCount, color: 'emerald' },
            { label: '수정', count: preview.updateCount, color: 'blue' },
            { label: '변경없음', count: preview.unchangedCount, color: 'gray' },
            { label: '에러', count: preview.errorCount, color: 'red' },
          ].map(({ label, count, color }) => (
            <div key={label} className={`p-4 bg-${color}-50 rounded-lg border border-${color}-200 text-center`}>
              <p className={`text-2xl font-bold text-${color}-700`}>{count}</p>
              <p className={`text-sm text-${color}-600`}>{label}</p>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2">
          {['all', 'new', 'update', 'unchanged', 'error'].map((f) => (
            <button
              key={f}
              onClick={() => setPreviewFilter(f)}
              className={`px-3 py-1 text-sm rounded-lg border ${
                previewFilter === f ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {f === 'all' ? '전체' : f === 'new' ? '신규' : f === 'update' ? '수정' : f === 'unchanged' ? '변경없음' : '에러'}
              {f === 'all' ? ` (${preview.totalRows})` : ''}
            </button>
          ))}
        </div>

        {/* Preview table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="max-h-[500px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Queture ID</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">학년-단원</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">상태</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">비고</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredPreviewRows.map((row, i) => (
                  <tr
                    key={i}
                    className={
                      row.status === 'new' ? 'bg-emerald-50/50' :
                      row.status === 'update' ? 'bg-blue-50/50' :
                      row.status === 'error' ? 'bg-red-50/50' : ''
                    }
                  >
                    <td className="px-4 py-2 font-mono text-xs">{row.quetureId}</td>
                    <td className="px-4 py-2">{row.grade}</td>
                    <td className="px-4 py-2">{statusBadge(row.status)}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">
                      {row.status === 'update' && row.previousGrade && (
                        <span>{row.previousGrade} → {row.grade}</span>
                      )}
                      {row.status === 'error' && (
                        <span className="text-red-600">{row.errorMessage}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => { setStep('table'); setPreview(null) }}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={handleBulkSave}
            disabled={saveableCount === 0 || bulkSaveMutation.isPending}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {bulkSaveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            저장 확인 ({saveableCount}건)
          </button>
        </div>
      </div>
    )
  }

  // ── Step: Table (Main) ─────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <h1 className="text-2xl font-bold text-gray-900">깨처 콘텐츠</h1>

      {/* Toolbar — 학년-단원 섹션 */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">학년-단원</span>
        <button
          onClick={() => quetureContentApi.downloadSampleCsv()}
          className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <Download className="w-4 h-4" /> 샘플 CSV
        </button>

        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-1.5 px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          <Upload className="w-4 h-4" /> CSV 업로드
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) handleFileUpload(file)
            e.target.value = ''
          }}
        />

        <button
          onClick={() => setAddingNew(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <Plus className="w-4 h-4" /> 개별 추가
        </button>

        <div className="h-6 w-px bg-gray-300" />

        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">빌드업</span>
        <button
          onClick={() => quetureContentApi.downloadBuildupSampleCsv()}
          className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <Download className="w-4 h-4" /> 샘플 CSV
        </button>
        <button
          onClick={() => buildupFileInputRef.current?.click()}
          className="flex items-center gap-1.5 px-3 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700"
        >
          <Upload className="w-4 h-4" /> CSV 업로드
        </button>
        <input
          ref={buildupFileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) handleBuildupFileUpload(file)
            e.target.value = ''
          }}
        />

        <div className="h-6 w-px bg-gray-300" />

        <div className="flex-1" />

        {/* Search */}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Queture ID 또는 학년-단원 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            onClick={handleSearch}
            className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Search className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 mr-1">필터:</span>
        {([
          { key: 'hasGrade' as const, label: '학년-단원' },
          { key: 'hasToon' as const, label: '깨봉툰' },
          { key: 'hasQueboard' as const, label: '깨보드' },
          { key: 'hasBuildup' as const, label: '빌드업' },
        ]).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => toggleFilter(key)}
            className={`px-3 py-1 text-xs rounded-full border transition-colors ${
              filters[key]
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}
        {Object.values(filters).some(Boolean) && (
          <button
            onClick={() => { setFilters({}); setPage(0) }}
            className="px-2 py-1 text-xs text-gray-400 hover:text-gray-600"
          >
            초기화
          </button>
        )}
      </div>

      {/* Drag & Drop overlay */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`relative ${dragOver ? '' : 'hidden'}`}
      >
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-indigo-50/80 backdrop-blur-sm border-4 border-dashed border-indigo-400 rounded-xl">
          <div className="text-center">
            <FileUp className="w-12 h-12 mx-auto text-indigo-500 mb-2" />
            <p className="text-lg font-medium text-indigo-700">CSV 파일을 놓으세요</p>
          </div>
        </div>
      </div>

      {/* Upload loading */}
      {previewMutation.isPending && (
        <div className="p-6 bg-white rounded-lg border shadow-sm text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-2" />
          <p className="text-gray-600">CSV 파일 분석 중...</p>
        </div>
      )}
      {buildupPreviewMutation.isPending && (
        <div className="p-6 bg-white rounded-lg border shadow-sm text-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600 mx-auto mb-2" />
          <p className="text-gray-600">빌드업 CSV 파일 분석 중...</p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">깨처</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 w-32">학년-단원</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-600 w-24" colSpan={2}>깨봉툰</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-600 w-28">깨보드</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-600 w-28">빌드업</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-600 w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {/* Add new row */}
                {addingNew && (
                  <tr className="bg-emerald-50/50">
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={newQuetureId}
                        onChange={(e) => setNewQuetureId(e.target.value)}
                        placeholder="Queture ID"
                        className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        autoFocus
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={newGrade}
                        onChange={(e) => setNewGrade(e.target.value)}
                        placeholder="학년-단원"
                        className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddNew()}
                      />
                    </td>
                    <td colSpan={4} />
                    <td className="px-4 py-2 text-center">
                      <button onClick={handleAddNew} className="p-1 text-emerald-600 hover:bg-emerald-100 rounded">
                        <Check className="w-4 h-4" />
                      </button>
                      <button onClick={() => setAddingNew(false)} className="p-1 text-gray-400 hover:bg-gray-100 rounded ml-1">
                        <X className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                )}

                {pageData?.content.map((item) => (
                  <tr key={item.quetureId} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1">
                          <span className="font-mono text-xs text-gray-500">{item.quetureId}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              navigator.clipboard.writeText(item.quetureId)
                              toast.success('Queture ID 복사됨')
                            }}
                            className="p-0.5 text-gray-300 hover:text-gray-600 rounded transition-colors"
                            title="Queture ID 복사"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                        <span className="text-sm text-gray-900 truncate max-w-[250px]" title={item.title ?? ''}>
                          {item.title ?? <span className="text-gray-300 italic">-</span>}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      {editingId === item.quetureId ? (
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') updateMutation.mutate({ quetureId: item.quetureId, grade: editValue })
                            if (e.key === 'Escape') setEditingId(null)
                          }}
                          className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          autoFocus
                        />
                      ) : (
                        <span
                          className={`cursor-pointer hover:bg-gray-100 px-2 py-1 rounded ${item.grade ? '' : 'text-gray-400 italic'}`}
                          onClick={() => { setEditingId(item.quetureId); setEditValue(item.grade ?? '') }}
                        >
                          {item.grade ?? '-'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className={item.hasQnIntro ? 'text-emerald-600' : 'text-gray-300'}>
                        {item.hasQnIntro ? '✓' : '✗'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className={item.hasQnOutro ? 'text-emerald-600' : 'text-gray-300'}>
                        {item.hasQnOutro ? '✓' : '✗'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button
                        onClick={() => setQueboardEditId(item.quetureId)}
                        className={`px-2 py-1 text-xs rounded-lg border transition-colors ${
                          item.hasQueboardConfig
                            ? 'bg-purple-50 text-purple-700 border-purple-300 hover:bg-purple-100'
                            : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100 hover:text-gray-600'
                        }`}
                      >
                        {item.hasQueboardConfig ? `${item.queboardProblemCount}개 수식` : '설정'}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button
                        onClick={() => setBuildupEditId(item.quetureId)}
                        className={`px-2 py-1 text-xs rounded-lg border transition-colors ${
                          item.hasBuildup
                            ? 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100'
                            : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100 hover:text-gray-600'
                        }`}
                      >
                        {item.hasBuildup ? `${item.buildupStepCount}개 스텝` : '설정'}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-center">
                      {editingId === item.quetureId ? (
                        <>
                          <button
                            onClick={() => updateMutation.mutate({ quetureId: item.quetureId, grade: editValue })}
                            className="p-1 text-emerald-600 hover:bg-emerald-100 rounded"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded ml-1">
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => { setEditingId(item.quetureId); setEditValue(item.grade ?? '') }}
                            className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm(`${item.quetureId}의 학년-단원을 삭제하시겠습니까?`)) {
                                deleteMutation.mutate(item.quetureId)
                              }
                            }}
                            className="p-1 text-gray-400 hover:bg-red-100 hover:text-red-600 rounded ml-1"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}

                {pageData?.content.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      데이터가 없습니다
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {pageData && pageData.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <p className="text-sm text-gray-600">
                  {page * 20 + 1}-{Math.min((page + 1) * 20, pageData.totalElements)} of {pageData.totalElements}
                </p>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="p-1.5 rounded border border-gray-300 hover:bg-white disabled:opacity-30"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage(Math.min(pageData.totalPages - 1, page + 1))}
                    disabled={page >= pageData.totalPages - 1}
                    className="p-1.5 rounded border border-gray-300 hover:bg-white disabled:opacity-30"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* QueBoard Config Editor Modal */}
      {queboardEditId && (
        <QueBoardConfigEditor
          quetureId={queboardEditId}
          onClose={() => setQueboardEditId(null)}
        />
      )}

      {/* Buildup Editor Modal */}
      {buildupEditId && (
        <BuildupEditor
          quetureId={buildupEditId}
          onClose={() => setBuildupEditId(null)}
        />
      )}
    </div>
  )
}
